import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Plus,
  Trash2,
  ExternalLink,
  ArrowRight,
  Copy,
  Filter,
  Route,
  Map as MapIcon,
  Users,
} from "lucide-react";
import {
  fetchSnapshots,
  fetchFacets,
  createSnapshot,
  deleteSnapshot,
} from "../lib/api";
import { buildGoogleMapsRouteUrl } from "../lib/routes";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { MultiSelectCombobox } from "../components/MultiSelectCombobox";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { value: "newest_opening", label: "Newest opening first" },
  { value: "oldest_opening", label: "Oldest opening first" },
  { value: "name", label: "Name A→Z" },
  { value: "recently_updated", label: "Recently updated" },
];

const CONFIDENCE_OPTIONS = [
  { value: "ALL", label: "Any" },
  { value: "HIGH", label: "High only" },
  { value: "MEDIUM", label: "Medium only" },
  { value: "LOW", label: "Low only" },
];

function CreateModal({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [facets, setFacets] = useState({ cities: [], counties: [] });
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [counties, setCounties] = useState([]); // multi-select
  const [cities, setCities] = useState([]);     // multi-select
  const [confidence, setConfidence] = useState("ALL");
  const [sort, setSort] = useState("newest_opening");
  const [limit, setLimit] = useState(20);
  const [openedAfter, setOpenedAfter] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) fetchFacets().then(setFacets);
  }, [open]);

  // Build city↔county lookup from the facets pairs so the two multi-
  // pickers stay in sync (pick a city → its county is added; pick a
  // county → city dropdown narrows to that county's cities).
  const { cityToCounty, countyToCities } = useMemo(() => {
    const cToCntyCounts = {};
    const cntyToCSet = {};
    (facets.pairs || []).forEach(({ city, county, count }) => {
      if (city && county) {
        cToCntyCounts[city] = cToCntyCounts[city] || {};
        cToCntyCounts[city][county] =
          (cToCntyCounts[city][county] || 0) + (count || 1);
        cntyToCSet[county] = cntyToCSet[county] || new Set();
        cntyToCSet[county].add(city);
      }
    });
    const cityToCounty = {};
    Object.entries(cToCntyCounts).forEach(([city, counts]) => {
      cityToCounty[city] = Object.entries(counts).sort(
        (a, b) => b[1] - a[1]
      )[0][0];
    });
    const countyToCities = {};
    Object.entries(cntyToCSet).forEach(([cnty, set]) => {
      countyToCities[cnty] = Array.from(set).sort();
    });
    return { cityToCounty, countyToCities };
  }, [facets.pairs]);

  // When ≥1 county is picked, the city picker narrows to the union of
  // cities in those counties.
  const cityOptions = useMemo(() => {
    if (counties.length === 0) return facets.cities || [];
    const allowed = new Set();
    counties.forEach((c) =>
      (countyToCities[c] || []).forEach((city) => allowed.add(city))
    );
    return Array.from(allowed).sort();
  }, [counties, countyToCities, facets.cities]);

  // Wrappers — linked behaviour.
  const handleCitiesChange = (next) => {
    setCities(next);
    // Auto-add the county of any newly picked city (union, dedupe).
    const additional = next.map((c) => cityToCounty[c]).filter(Boolean);
    if (additional.length) {
      setCounties((prev) => Array.from(new Set([...prev, ...additional])));
    }
  };
  const handleCountiesChange = (next) => {
    setCounties(next);
    // Drop selected cities that are no longer in an allowed county.
    if (next.length > 0) {
      const allowed = new Set();
      next.forEach((c) =>
        (countyToCities[c] || []).forEach((city) => allowed.add(city))
      );
      setCities((prev) =>
        prev.filter((city) => !cityToCounty[city] || allowed.has(city))
      );
    }
  };

  // Auto-suggest title based on selected counties / cities.
  useEffect(() => {
    if (!title) {
      if (counties.length === 1) setTitle(`Newest dispensaries in ${counties[0]} County`);
      else if (counties.length > 1) setTitle(`Newest dispensaries in ${counties.length} counties`);
      else if (cities.length === 1) setTitle(`Newest dispensaries in ${cities[0]}`);
      else if (cities.length > 1) setTitle(`Newest dispensaries in ${cities.length} cities`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counties, cities]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Please enter a snapshot title");
      return;
    }
    try {
      setSubmitting(true);
      const snap = await createSnapshot({
        title: title.trim(),
        subtitle: subtitle.trim(),
        assigned_to: assignedTo.trim(),
        filters: {
          county: counties.length ? counties : null,
          city: cities.length ? cities : null,
          confidence: confidence !== "ALL" ? confidence : null,
          sort,
          limit: Math.max(1, Math.min(Number(limit) || 20, 100)),
          opened_after: openedAfter || null,
          include_closed: false,
        },
      });
      toast.success(`Lead list created · ${snap.total_count} dispensaries`);
      setOpen(false);
      setTitle("");
      setSubtitle("");
      setAssignedTo("");
      onCreated?.(snap);
    } catch (e) {
      toast.error("Failed to create snapshot");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-snapshot-btn"
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Lead List
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="new-snapshot-modal"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50">
            Create a Lead Generator Report
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 mt-1">
            A clean, shareable list of dispensary leads — perfect for weekly
            sales meetings. Closed dispensaries are always excluded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Title
            </Label>
            <Input
              data-testid="snapshot-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Newest dispensaries in Los Angeles County"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Subtitle (optional)
            </Label>
            <Input
              data-testid="snapshot-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="For weekly sales meeting · Feb 21"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Assigned to (optional)
            </Label>
            <Input
              data-testid="snapshot-assigned-to"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              placeholder="e.g. Maria · Joe (bus driver)"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3 pt-1 items-start">
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                County
              </Label>
              <MultiSelectCombobox
                testId="snapshot-county"
                value={counties}
                onChange={handleCountiesChange}
                options={facets.counties}
                placeholder="Any county · type or pick"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                City{counties.length > 0 ? ` · in ${counties.length === 1 ? counties[0] : counties.length + " counties"}` : ""}
              </Label>
              <MultiSelectCombobox
                testId="snapshot-city"
                value={cities}
                onChange={handleCitiesChange}
                options={cityOptions}
                placeholder={
                  counties.length > 0
                    ? `Any city in ${counties.length === 1 ? counties[0] : "selected counties"}`
                    : "Any city · type or pick"
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                Confidence
              </Label>
              <Select value={confidence} onValueChange={setConfidence}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {CONFIDENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                Sort
              </Label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger
                  data-testid="snapshot-sort"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                Opened after
              </Label>
              <Input
                type="date"
                data-testid="snapshot-opened-after"
                value={openedAfter}
                onChange={(e) => setOpenedAfter(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono h-9 leading-none appearance-none"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 h-4 leading-4">
                Max dispensaries
              </Label>
              <Input
                type="number"
                min={1}
                max={100}
                data-testid="snapshot-limit"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono h-9 leading-none appearance-none"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="create-snapshot-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
          >
            {submitting ? "Generating…" : "Generate lead list"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Snapshots() {
  const [items, setItems] = useState(null);

  const load = async () => setItems(await fetchSnapshots());
  useEffect(() => {
    load();
  }, []);

  const shareUrl = (id) =>
    `${window.location.origin}/snapshots/${id}`;

  const copyLink = (id) => {
    navigator.clipboard.writeText(shareUrl(id));
    toast.success("Link copied", { description: "Paste into your meeting chat" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead list?")) return;
    await deleteSnapshot(id);
    toast.success("Lead list deleted");
    load();
  };

  const counts = useMemo(() => {
    if (!items) return { total: 0 };
    return { total: items.length };
  }, [items]);

  return (
    <div className="max-w-[1600px] mx-auto space-y-5 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Lead Generator</h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl leading-relaxed">
            Clean, presentation-ready lists of dispensary leads for your weekly
            sales meetings. Frozen at creation time — share the link, everyone
            sees the same data.{" "}
            <span className="text-zinc-400 font-mono text-xs">
              ({counts.total} saved)
            </span>
          </p>
        </div>
        <CreateModal onCreated={load} />
      </div>

      <div className="panel overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono border-b border-zinc-800 bg-zinc-950/40">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Filters</div>
          <div className="col-span-1 text-right">Leads</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div data-testid="snapshot-list">
          {items ? (
            items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-zinc-500">
                <FileText className="w-8 h-8 mx-auto text-zinc-700 mb-3" />
                No lead lists yet — generate your first one for your next sales
                meeting.
              </div>
            ) : (
              items.map((s) => {
                const f = s.filters || {};
                const countyList = Array.isArray(f.county) ? f.county : f.county ? [f.county] : [];
                const cityList = Array.isArray(f.city) ? f.city : f.city ? [f.city] : [];
                const filterBits = [
                  countyList.length === 1 && `${countyList[0]} Co.`,
                  countyList.length > 1 && `${countyList.length} counties`,
                  cityList.length === 1 && cityList[0],
                  cityList.length > 1 && `${cityList.length} cities`,
                  f.dispensary_ids && f.dispensary_ids.length && `${f.dispensary_ids.length} hand-picked`,
                  f.confidence && `${f.confidence}+`,
                ]
                  .filter(Boolean)
                  .join(" · ");
                const mapUrl = buildGoogleMapsRouteUrl(s.dispensaries || []);
                return (
                  <div
                    key={s.id}
                    data-testid={`snapshot-row-${s.id}`}
                    className="grid grid-cols-12 gap-3 px-4 py-3 text-sm border-b border-zinc-900 last:border-b-0 hover:bg-zinc-800/30 transition-colors items-center"
                  >
                    <div className="col-span-5">
                      <Link
                        to={`/snapshots/${s.id}`}
                        className="text-zinc-50 font-medium hover:text-emerald-300 inline-flex items-center gap-2"
                      >
                        {s.title}
                        {s.assigned_to && (
                          <span
                            data-testid={`snapshot-assigned-${s.id}`}
                            className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-sky-300 bg-sky-500/10 border border-sky-500/30 px-1.5 py-0.5 rounded"
                            title="Assigned to"
                          >
                            <Users className="w-2.5 h-2.5" />
                            {s.assigned_to}
                          </span>
                        )}
                      </Link>
                      {s.subtitle && (
                        <div className="text-xs text-zinc-500 truncate">
                          {s.subtitle}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 text-xs text-zinc-400 font-mono inline-flex items-center gap-1 truncate">
                      <Filter className="w-3 h-3 text-zinc-600" />
                      {filterBits || "—"}
                    </div>
                    <div className="col-span-1 text-right font-mono text-emerald-300">
                      {s.total_count}
                    </div>
                    <div className="col-span-2 text-xs text-zinc-400 font-mono">
                      {s.created_at?.slice(0, 10)}
                    </div>
                    <div className="col-span-2 flex justify-end items-center gap-1.5">
                      {mapUrl && (
                        <a
                          data-testid={`snapshot-plan-route-${s.id}`}
                          href={mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-zinc-400 hover:text-emerald-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                          title="Plan first 9 stops in Google Maps"
                        >
                          <Route className="w-3 h-3" />
                        </a>
                      )}
                      {s.dispensary_ids?.length > 0 && (
                        <Link
                          data-testid={`snapshot-view-on-map-${s.id}`}
                          to={`/map?ids=${encodeURIComponent(s.dispensary_ids.join(","))}`}
                          className="text-xs text-zinc-400 hover:text-emerald-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                          title="View all stops on map (handles routes >9 stops)"
                        >
                          <MapIcon className="w-3 h-3" />
                        </Link>
                      )}
                      <button
                        data-testid={`copy-link-${s.id}`}
                        onClick={() => copyLink(s.id)}
                        className="text-xs text-zinc-400 hover:text-emerald-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                        title="Copy share link"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <Link
                        to={`/snapshots/${s.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-zinc-400 hover:text-emerald-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                        title="Open snapshot in new tab"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <Link
                        to={`/snapshots/${s.id}`}
                        className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-800"
                      >
                        Open
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                      <button
                        data-testid={`delete-snapshot-${s.id}`}
                        onClick={() => remove(s.id)}
                        className="text-xs text-zinc-500 hover:text-rose-400 px-2 py-1 rounded hover:bg-zinc-800"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            [0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 my-1 mx-2 rounded-md bg-zinc-900/40" />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
